import {useEffect, useRef, useState} from "react"
import {CircularProgress, Divider, Stack, Typography, useTheme} from "@mui/joy"
import {Link, useNavigate, useSearchParams} from "react-router-dom"
import {AlertCircle, ArrowLeft, ShieldCheck} from "lucide-react"
import {useTranslation} from "react-i18next"
import Card from "../components/Card"
import Button from "../components/Button"
import PageContainer from "../components/layout/PageContainer"
import IkeepayCheckout from "../components/IkeepayCheckout"
import PaymentStatusDialog, {PaymentDialogStatus} from "../components/PaymentStatusDialog"
import {useAsync} from "../hooks/useAsyncResult"
import {useAuth} from "../hooks/useAuth"
import {checkTransactionStatus, createCheckout, initPayment} from "../services/api/transaction"
import {WS_API_HOST} from "../constants"
import {activePalette, applyTransparency} from "../theme"
import {PLANS, formatAmount, planOf} from "../values/plans"
import {PlanTier} from "../types/subscription"

/**
 * Where a plan is paid for.
 *
 * The amount is never sent: the API prices the plan and applies whatever referral discount the
 * account has, so this screen only chooses the plan. The payment itself is collected by Ikeepay's
 * own checkout, opened over the page, so no card or phone number passes through this app.
 */
const Checkout = () => {
    const {t, i18n} = useTranslation()
    const palette = activePalette(useTheme())
    const auth = useAuth()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    const checkout = useAsync(initPayment, true, false, true)
    const session = useAsync(createCheckout, true, true, false)
    const status = useAsync(checkTransactionStatus, true, false, false)

    const [checkoutUrl, setCheckoutUrl] = useState<string>()
    const [error, setError] = useState("")
    const [transactionId, setTransactionId] = useState<string>()
    const [paymentStatus, setPaymentStatus] = useState<PaymentDialogStatus>()
    const [dialogOpen, setDialogOpen] = useState(false)
    /** Closed on the way out, so a payment left behind does not keep a socket open */
    const socketRef = useRef<WebSocket>(null)

    const tier = (searchParams.get("plan") || "platinum") as PlanTier
    const plan = planOf(tier)
    const init = checkout.data?.data
    const offer = init?.plans?.find(value => value.tier === tier)
    const discountPercent = init?.discountPercent
    const listPrice = offer?.price ?? plan.price
    // The API charges this, the page only says so: the discount is granted server side
    const total = discountPercent ? Math.round(listPrice * (100 - discountPercent) / 100) : listPrice
    const amountLabel = `${formatAmount(total)} ${t("currencyLabel")}`

    /**
     * The plans are what this screen is drawn from, so there is nothing to buy until they load.
     * Told apart from an empty catalogue on purpose: one is worth retrying, the other is not.
     */
    const checkoutFailed = !checkout.loading && !init

    useEffect(() => {
        checkout.execute()

        return () => socketRef.current?.close()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * Waits on the payment.
     *
     * Ikeepay answers the webhook, not the browser, so the outcome is pushed over the socket. A
     * socket that cannot be opened, or that times out, leaves the status button as the way out.
     */
    const listen = (id: string, initialStatus: PaymentDialogStatus) => {
        setTransactionId(id)
        setPaymentStatus(initialStatus)

        try {
            const socket = new WebSocket(`${WS_API_HOST}/ws/payments?transactionId=${id}`)
            socketRef.current = socket

            socket.onmessage = event => {
                const answer = JSON.parse(event.data)
                setPaymentStatus(answer.status)

                if (answer.status !== "pending") {
                    setCheckoutUrl(undefined)
                    setDialogOpen(true)
                }

                // The plan is written on the account by the API, the session has to be read again
                if (answer.status === "successful")
                    auth.refresh?.()
            }
        } catch (e) {
            // The dialog still offers to ask for the status itself
        }
    }

    /** Prices the plan server side, then hands the payer over to Ikeepay */
    const pay = async () => {
        setError("")

        const result = (await session.execute(tier)) || undefined

        if (!result?.data?.checkoutUrl)
            return

        listen(result.data.transactionId, result.data.initialStatus as PaymentDialogStatus)
        setCheckoutUrl(result.data.checkoutUrl)
    }

    /** What the checkout frame reports, confirmed against what the API has actually recorded */
    const checkoutSucceeded = async () => {
        setCheckoutUrl(undefined)
        setPaymentStatus("pending")
        setDialogOpen(true)
        await refreshStatus()
    }

    const refreshStatus = async () => {
        if (!transactionId) return

        const result = (await status.execute(transactionId)) || undefined

        if (result?.data?.status) {
            setPaymentStatus(result.data.status)

            if (result.data.status === "successful")
                auth.refresh?.()
        }
    }

    return <PageContainer gap={3} width={{md: "38rem", lg: "48rem"}}>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
            <Link to={"/pricing"} style={{textDecoration: "none", display: "flex"}} aria-label={t("backToPlans")}>
                <ArrowLeft size={20}/>
            </Link>
            <Typography level={"h2"} fontSize={{xs: "1.6rem", sm: undefined}}>{t("checkoutTitle")}</Typography>
        </Stack>
        <Typography level={"body-sm"}>{t("checkoutSubtitle")}</Typography>

        {/* Order */}
        <Card>
            <Typography level={"title-lg"}>{t("orderSummary")}</Typography>

            <Stack direction={"row"} gap={1} flexWrap={"wrap"}>
                {(init?.plans?.length ? init.plans : PLANS.map(value => ({
                    tier: value.tier,
                    name: value.name,
                    price: value.price
                }))).map(value =>
                    <Stack key={value.tier} paddingX={2} paddingY={1}
                           onClick={() => setSearchParams({plan: value.tier})}
                           sx={{
                               cursor: "pointer",
                               borderRadius: "12px",
                               border: `1px solid ${value.tier === tier
                                   ? palette.primary["600"]
                                   : "rgba(255,255,255,0.1)"}`,
                               background: value.tier === tier
                                   ? applyTransparency(palette.primary["500"], 0.18)
                                   : "transparent"
                           }}>
                        <Typography level={"title-sm"}>{value.name}</Typography>
                        <Typography level={"body-xs"}>
                            {formatAmount(value.price)} {t("currencyLabel")}
                        </Typography>
                    </Stack>)}
            </Stack>

            <Divider/>

            {/* The lines the API sends win: they are what the plan is currently sold on */}
            <Stack gap={1}>
                {(offer?.features?.length
                    ? offer.features.map(feature => feature[i18n.language === "en" ? "en" : "fr"] || feature.fr)
                    : plan.features.slice(0, 3).map(feature => t(feature)))
                    .map((feature, index) =>
                        <Typography key={`${tier}-${index}`} level={"body-xs"}>· {feature}</Typography>)}
            </Stack>

            {!!discountPercent && <Typography level={"body-sm"} textColor={"success.400"}>
                {t("discountApplied", {percent: discountPercent})}
            </Typography>}

            <Divider/>

            <Stack direction={"row"} justifyContent={"space-between"} alignItems={"baseline"} flexWrap={"wrap"}
                   gap={1}>
                <Typography level={"title-md"}>{t("totalLabel")}</Typography>
                <Typography level={"h3"}>{amountLabel}</Typography>
            </Stack>
        </Card>

        {/* Payment */}
        <Card>
            {checkout.loading
                ? <Typography startDecorator={<CircularProgress size={"sm"}/>}>{t("loadingCheckout")}</Typography>
                : checkoutFailed
                    // Nothing on this screen works without the catalogue, so it says so and offers a way back
                    ? <Stack gap={2} alignItems={"flex-start"}>
                        <Typography level={"body-sm"} color={"danger"} startDecorator={<AlertCircle size={16}/>}>
                            {t("checkoutUnavailable")}
                        </Typography>
                        <Button variant={"soft"} onClick={() => checkout.execute()}>{t("retry")}</Button>
                    </Stack>
                    : <>
                        <Typography level={"body-sm"}>{t("paymentMethodsNotice")}</Typography>

                        {(error || session.error) && <Typography level={"body-sm"} color={"danger"}>
                            {error || session.error}
                        </Typography>}

                        <Button size={"lg"} loading={session.loading} onClick={pay}>
                            {t("payNow", {amount: amountLabel})}
                        </Button>

                        <Typography level={"body-xs"} startDecorator={<ShieldCheck size={14}/>}>
                            {t("paymentTerms", {amount: amountLabel})}
                        </Typography>
                    </>}
        </Card>

        <IkeepayCheckout checkoutUrl={checkoutUrl}
                         onSuccess={checkoutSucceeded}
                         onClose={() => setCheckoutUrl(undefined)}
                         onError={message => {
                             setCheckoutUrl(undefined)
                             setError(message || t("unknownErrorMessage"))
                         }}/>

        <PaymentStatusDialog open={dialogOpen}
                             status={paymentStatus}
                             statusLoading={status.loading}
                             onCheckStatus={refreshStatus}
                             onClose={() => setDialogOpen(false)}
                             onDone={() => navigate("/analyze")}/>
    </PageContainer>
}

export default Checkout
